import { AnyAction } from '@reduxjs/toolkit'

import {
  ADD_IDP_SUCCESS,
  DELETE_IDP_SUCCESS,
  FETCH_SAML_IDP_CONFIG_SUCCESS,
  UPDATE_IDP_SUCCESS
} from '@/pages/OrganizationSecurityPage/actionTypes'
import { CHANGE_DEFAULT_SPACE_SUCCESS, UPDATE_ORGANIZATION_SUCCESS } from '@/pages/OrganizationSettingsPage/actionTypes'
import {
  ADD_SPACE_ADMIN_SUCCESS,
  ADD_SPACE_SUCCESS,
  REMOVE_SPACE_ADMIN_SUCCESS,
  UPDATE_SPACE_SUCCESS
} from '@/pages/OrganizationSpaceEditPage/actionTypes'
import { DELETE_SPACE_SUCCESS } from '@/pages/OrganizationSpacesPage/actionTypes'
import { UPDATE_USER_SUCCESS } from '@/pages/OrganizationUserEditPage/actionTypes'
import { UPDATE_SPACE_ROLE_SUCCESS } from '@/pages/SpaceMemberEditPage/actionTypes'
import { REMOVE_MEMBER_FROM_SPACE_SUCCESS } from '@/pages/SpaceMembersPage/actionTypes'
import {
  ADD_SPACE_MEMBER_FROM_ROLE_SUCCESS,
  REMOVE_SPACE_MEMBER_FROM_ROLE_SUCCESS
} from '@/pages/SpaceMembersRolesPage/actionTypes'
import { CHANGE_CURRENT_SPACE, SELECT_EDIT_SPACE_MEMBER } from '@/store/actionTypes'
import clientLocalStorage from '@/utilities/clientLocalStorage'

import { FETCH_CONTEXT_ORGANIZATION_SUCCESS, FETCH_CONTEXT_USER_SUCCESS } from './actionTypes'

export type Organization = {
  id: string
  spaces: any[]
  identityProviders: any[]
  accountManagementConnectors: any[]
  companyCode: string
  supportUrl?: string
  logoUrl: string
  name: string
  planId: string
  userPool?: any
}

export type UserRoles = {
  organization?: Record<string, { id: string; name: string }[]>
  spaces?: Record<string, { id: string; name: string }[]>
}

type AppState = {
  organization: Organization
  spaceId: string
  userId?: string
  userRoles?: UserRoles
  spaceIdEdit?: string
  SpaceMemberIdEdit?: string
}

const initialState: AppState = {
  organization: {} as Organization,
  spaceId: undefined!,
  userId: undefined,
  userRoles: undefined
}

export default function appReducer(state = initialState, action: AnyAction): AppState {
  switch (action.type) {
    case FETCH_CONTEXT_USER_SUCCESS: {
      return {
        ...state,
        // set the organization id as this may have returned before the context organization request
        organization: { ...state.organization, id: action.response.organizationId },
        // use the EXISTING organization state to determine the current space so we can detect if the organization context has loaded
        spaceId: determineSpaceId(state.organization, action.response.roles, state.spaceId),
        userId: action.response.id,
        userRoles: action.response.roles
      }
    }

    case FETCH_CONTEXT_ORGANIZATION_SUCCESS: {
      return {
        ...state,
        organization: {
          accountManagementConnectors: action.response.accountManagementConnectors,
          companyCode: action.response.companyCode,
          id: action.response.id,
          identityProviders: action.response.identityProviders,
          supportUrl: action.response.supportUrl,
          logoUrl: action.response.logoUrl,
          name: action.response.name,
          planId: action.response.planId,
          spaces: action.response.spaces
        },
        spaceId: determineSpaceId(action.response, state.userRoles, state.spaceId)
      }
    }

    case CHANGE_CURRENT_SPACE: {
      return { ...state, spaceId: action.spaceId }
    }

    case SELECT_EDIT_SPACE_MEMBER: {
      return { ...state, SpaceMemberIdEdit: action.userId }
    }

    case UPDATE_SPACE_ROLE_SUCCESS:
    case UPDATE_USER_SUCCESS:
    case ADD_SPACE_MEMBER_FROM_ROLE_SUCCESS:
    case ADD_SPACE_ADMIN_SUCCESS:
    case REMOVE_SPACE_ADMIN_SUCCESS:
    case REMOVE_SPACE_MEMBER_FROM_ROLE_SUCCESS: {
      return updateUserRoles(state, action.response)
    }

    case REMOVE_MEMBER_FROM_SPACE_SUCCESS: {
      return removeUserFromSpace(state, action)
    }

    case FETCH_SAML_IDP_CONFIG_SUCCESS: {
      return { ...state, organization: { ...state.organization, userPool: action.response } }
    }

    case ADD_IDP_SUCCESS: {
      return {
        ...state,
        organization: {
          ...state.organization,
          identityProviders: [...state.organization.identityProviders, action.response]
        }
      }
    }

    case UPDATE_IDP_SUCCESS: {
      return updateIdp(state, action)
    }

    case DELETE_IDP_SUCCESS: {
      return {
        ...state,
        organization: {
          ...state.organization,
          identityProviders: state.organization.identityProviders.filter(idp => idp.id !== action.identityProviderId)
        }
      }
    }

    case DELETE_SPACE_SUCCESS: {
      return {
        ...state,
        organization: {
          ...state.organization,
          spaces: state.organization.spaces.filter(space => space.id !== action.spaceId)
        }
      }
    }

    case ADD_SPACE_SUCCESS: {
      return {
        ...state,
        organization: {
          ...state.organization,
          spaces: state.organization.spaces.concat(action.response)
        },
        spaceIdEdit: action.response.id
      }
    }

    case UPDATE_ORGANIZATION_SUCCESS: {
      return { ...state, organization: { ...state.organization, ...action.response } }
    }

    case UPDATE_SPACE_SUCCESS: {
      return updateSpace(state, action)
    }

    case CHANGE_DEFAULT_SPACE_SUCCESS: {
      return {
        ...state,
        organization: {
          ...state.organization,
          spaces: state.organization.spaces.map(space => {
            return { ...space, default: action.spaceId === space.id }
          })
        }
      }
    }

    default: {
      return state
    }
  }
}

const determineSpaceId = (
  organization: Pick<Organization, 'id' | 'spaces'>,
  roles?: UserRoles,
  selectedSpaceId?: string
) => {
  const isMember = (spaceId: string) => spaceId in (roles?.spaces ?? {})

  const getDefaultSpace = () => {
    let defaultSpace = organization.spaces.find(space => space.default && isMember(space.id))

    if (!defaultSpace) {
      // user doesn't have a default space so we simply pick the first one
      defaultSpace = organization.spaces.find(space => isMember(space.id))
    }

    // handle the case of a user not having access to ANY spaces
    return defaultSpace ? defaultSpace.id : null
  }

  const localStorageSpace = clientLocalStorage.getItem('selectedSpace')

  /*
   * Both the user and organization context requests have to have completed to determine the current space. However, the
   * current space is defaulted when the URL includes a space identifier so we must save this identifier temporarily
   * while the context information is loading.
   */
  if (!organization.id) return selectedSpaceId

  const hasValidSelectedSpace = selectedSpaceId && isMember(selectedSpaceId)
  const hasValidLocalStorageSpace = localStorageSpace && isMember(localStorageSpace)

  if (hasValidSelectedSpace) {
    return selectedSpaceId
  }

  if (!hasValidSelectedSpace && hasValidLocalStorageSpace) {
    return localStorageSpace
  }

  return getDefaultSpace()
}

function updateUserRoles(state: AppState, user: any) {
  if (state.userId !== user.id) return state

  return {
    ...state,
    spaceId: determineSpaceId(state.organization, user.roles, state.spaceId),
    userRoles: user.roles
  }
}

function removeUserFromSpace(state: AppState, action: AnyAction) {
  if (state.userId !== action.userId) return state

  const userRoles = { ...state.userRoles }
  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
  if (userRoles.spaces) delete userRoles.spaces[action.spaceId]

  return {
    ...state,
    spaceId: action.spaceId === state.spaceId ? determineSpaceId(state.organization, userRoles) : state.spaceId,
    userRoles
  }
}

function updateIdp(state: AppState, action: AnyAction) {
  const identityProviders = state.organization.identityProviders.map(idp => {
    if (idp.id === action.response.id) {
      return action.response
    } else {
      return idp
    }
  })

  return {
    ...state,
    organization: { ...state.organization, identityProviders }
  }
}

function updateSpace(state: AppState, action: AnyAction) {
  const spaces = state.organization.spaces.map(space => {
    if (space.id === action.response.id) {
      return { ...space, ...action.response }
    } else {
      return space
    }
  })

  return {
    ...state,
    spaces
  }
}
